import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { ArrowIcon, ChevronIcon, money } from './widgetParts';

// The minimal widget (layout=minimal): no background, no boxes, just text and hairlines, so that it takes on whatever it is
// placed on. Every colour is the one text colour (light for a dark page, dark for a light one) at different strengths; the only
// other colour is a sector's own, as the underline of the sector that is open.
//
//   Careers for Construction students                       Careers   Video   Ask our AI
//   ──────────────────────────────────────────────────────────────────────────────────
//   Electrical 6    Carpentry 3    Roofing 3    Building 4                         ›
//   ━━━━━━━━━━                                                         │  Skilldar
//   CCTV Installer                  £33,792    1-2 years    6 courses  →  │   (chart)
//   ──────────────────────────────────────────────────────────────────  │
//   Fire Alarm Engineer             £31,450    1-2 years    4 courses  →  │
//   ──────────────────────────────────────────────────────────────────────────────────
//   Powered by ThinkSkills                                     1-5 of 12   ‹  ›
const STYLES = `
html, body, #root { background: transparent !important; }
.tsw-m { --fg:18,21,28; --ink:rgb(var(--fg)); --soft:rgba(var(--fg),.68); --faint:rgba(var(--fg),.5); --line:rgba(var(--fg),.2); color:var(--ink); background:transparent; font-family:inherit; font-size:14px; text-align:left; width:100%; padding:2px; box-sizing:border-box; display:flex; flex-direction:column; }
.tsw-m.tone-light { --fg:255,255,255; }
.tsw-m *, .tsw-m *::before, .tsw-m *::after { box-sizing:border-box; }
.tsw-m.is-fill { height:100%; }
.tsw-m button { font:inherit; color:inherit; }

/* with a fixed height only the list of careers gives way (it scrolls) */
.tsw-m-main > * { flex-shrink:0; }
.tsw-m.is-fill .tsw-m-rows { flex:1 1 auto; min-height:0; overflow-y:auto; scrollbar-width:thin; }
.tsw-m-head { display:flex; align-items:baseline; justify-content:space-between; gap:6px 20px; flex-wrap:wrap; padding-bottom:10px; border-bottom:1px solid var(--line); }
.tsw-m-title { margin:0; text-align:left; text-wrap:balance; font-size:15px; font-weight:600; line-height:1.3; letter-spacing:.005em; color:var(--ink); }
.tsw-m-tabs { display:flex; gap:18px; }
.tsw-m-tab { appearance:none; background:none; border:0; padding:2px 0; font-size:13px; color:var(--soft); cursor:pointer; border-bottom:1px solid transparent; transition:color .15s, border-color .15s; }
.tsw-m-tab:hover { color:var(--ink); }
.tsw-m-tab[aria-selected="true"] { color:var(--ink); font-weight:600; border-bottom-color:var(--ink); }

.tsw-m-body { display:flex; gap:24px; flex:1 1 auto; min-height:0; }
.tsw-m.is-narrow .tsw-m-body { flex-direction:column; gap:0; }
.tsw-m-main { flex:1 1 auto; min-width:0; display:flex; flex-direction:column; }
.tsw-m-side { flex:0 0 260px; border-left:1px solid var(--line); padding:14px 0 10px; display:flex; flex-direction:column; align-items:stretch; }
.tsw-m.is-narrow .tsw-m-side { flex:0 0 auto; border-left:0; border-top:1px solid var(--line); padding:12px 0 0; }
.tsw-m-label { margin:0; font-size:11px; letter-spacing:.09em; text-transform:uppercase; font-weight:600; color:var(--soft); text-align:center; }
.tsw-m-hint { margin:3px 0 2px; font-size:12.5px; line-height:1.45; color:var(--soft); text-align:center; text-wrap:pretty; }
.tsw-m-chart { display:flex; justify-content:center; align-items:center; min-height:220px; width:100%; }
/* the same column and chart size as the wide layout */
.tsw-m:not(.is-narrow) .tsw-m-label, .tsw-m:not(.is-narrow) .tsw-m-hint { padding:0 20px; }
.tsw-m:not(.is-narrow) .tsw-m-chart { width:calc(100% + 20px); margin:0 -10px; }

.tsw-m-sectorwrap { position:relative; border-bottom:1px solid var(--line); }
.tsw-m-sectors { display:flex; gap:22px; overflow-x:auto; scrollbar-width:none; -ms-overflow-style:none; scroll-behavior:smooth; }
.tsw-m-sectors::-webkit-scrollbar { display:none; }
.tsw-m-sectors.fade-right { -webkit-mask-image:linear-gradient(90deg,#000 calc(100% - 56px),transparent); mask-image:linear-gradient(90deg,#000 calc(100% - 56px),transparent); }
.tsw-m-sectors.fade-left { -webkit-mask-image:linear-gradient(270deg,#000 calc(100% - 56px),transparent); mask-image:linear-gradient(270deg,#000 calc(100% - 56px),transparent); }
.tsw-m-sectors.fade-left.fade-right { -webkit-mask-image:linear-gradient(90deg,transparent,#000 56px,#000 calc(100% - 56px),transparent); mask-image:linear-gradient(90deg,transparent,#000 56px,#000 calc(100% - 56px),transparent); }
.tsw-m-sector { appearance:none; display:inline-flex; align-items:baseline; flex:0 0 auto; background:none; border:0; border-bottom:2px solid transparent; margin-bottom:-1px; padding:10px 0 9px; font-size:13px; color:var(--soft); cursor:pointer; white-space:nowrap; text-align:left; transition:color .15s, border-color .2s; }
.tsw-m-sector:hover { color:var(--ink); }
.tsw-m-sector[aria-pressed="true"] { color:var(--ink); font-weight:600; border-bottom-color:var(--sector, var(--ink)); }
.tsw-m-count { margin-left:5px; font-size:11.5px; font-weight:400; color:var(--faint); font-variant-numeric:tabular-nums; }
.tsw-m-snav { position:absolute; top:50%; transform:translateY(-50%); z-index:2; width:24px; height:24px; padding:0; border-radius:50%; border:1px solid var(--line); background:transparent; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s; }
.tsw-m-snav:hover { background:rgba(var(--fg),.12); }
.tsw-m-snav svg { width:12px; height:12px; }
.tsw-m-snav.is-left { left:0; }
.tsw-m-snav.is-right { right:0; }

.tsw-m-rows { list-style:none; margin:0; padding:0; }
.tsw-m-row { border-bottom:1px solid var(--line); animation:tsw-m-rise .28s cubic-bezier(.2,.7,.2,1) both; }
.tsw-m-link { display:grid; grid-template-columns:minmax(0,1fr) auto 16px; align-items:baseline; column-gap:18px; padding:11px 0 10px; color:var(--ink); text-decoration:none; }
.tsw-m-name { min-width:0; font-size:13px; font-weight:600; line-height:1.3; text-align:left; overflow-wrap:anywhere; text-wrap:pretty; }
.tsw-m-link:hover .tsw-m-name { text-decoration:underline; text-underline-offset:4px; text-decoration-thickness:1px; }
.tsw-m-meta { display:flex; gap:16px; font-size:12px; color:var(--soft); font-variant-numeric:tabular-nums; white-space:nowrap; }
.tsw-m-meta span { display:inline-block; text-align:right; }
.tsw-m-meta .m-pay { min-width:100px; }
.tsw-m-meta .m-time { min-width:96px; }
.tsw-m-meta .m-courses { min-width:74px; }
.tsw-m-arrow { width:15px; height:15px; align-self:center; color:var(--faint); transition:transform .18s, color .18s; }
.tsw-m-link:hover .tsw-m-arrow { transform:translateX(3px); color:var(--ink); }
.tsw-m.is-narrow .tsw-m-link { grid-template-columns:minmax(0,1fr) 16px; row-gap:3px; padding:10px 0 9px; }
.tsw-m.is-narrow .tsw-m-meta { grid-column:1; grid-row:2; gap:14px; }
.tsw-m.is-narrow .tsw-m-meta span { text-align:left; min-width:0 !important; }
.tsw-m.is-narrow .tsw-m-arrow { grid-column:2; grid-row:1; }
@keyframes tsw-m-rise { from { opacity:0; transform:translateY(5px); } to { opacity:1; transform:none; } }
@keyframes tsw-m-next { from { opacity:0; transform:translateX(24px); } to { opacity:1; transform:none; } }
@keyframes tsw-m-prev { from { opacity:0; transform:translateX(-24px); } to { opacity:1; transform:none; } }
.tsw-m-rows.dir-next .tsw-m-row { animation-name:tsw-m-next; }
.tsw-m-rows.dir-prev .tsw-m-row { animation-name:tsw-m-prev; }

.tsw-m-foot { display:flex; align-items:center; justify-content:space-between; gap:12px; padding-top:9px; font-size:11.5px; color:var(--faint); margin-top:auto; }
.tsw-m-pager { display:flex; align-items:center; gap:8px; margin-left:auto; }
.tsw-m-range { font-variant-numeric:tabular-nums; color:var(--soft); }
.tsw-m-nav { appearance:none; width:26px; height:26px; padding:0; border-radius:50%; border:1px solid var(--line); background:transparent; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s, opacity .15s; }
.tsw-m-nav:hover:not(:disabled) { background:rgba(var(--fg),.12); }
.tsw-m-nav:disabled { opacity:.3; cursor:default; }
.tsw-m-nav svg { width:13px; height:13px; }
.tsw-m-panel { padding-top:12px; flex:1 1 auto; min-height:0; overflow:auto; }
.tsw-m-empty { margin:0; padding:22px 0; color:var(--soft); }

.tsw-m a:focus-visible, .tsw-m button:focus-visible { outline:2px solid var(--ink); outline-offset:2px; border-radius:3px; }
@media (prefers-reduced-motion: reduce) {
  .tsw-m-row { animation:none; }
  .tsw-m *, .tsw-m *::before, .tsw-m *::after { transition:none !important; }
}
`;

const TABS = [
  { step: 0, label: 'Careers' },
  { step: 1, label: 'Video' },
  { step: 2, label: 'Ask our AI' },
];
const PAGE_SIZE = 5;

const MinimalLayout = ({
  sectors,
  loading,
  heading,
  onOpenCareer,
  currentStep,
  onStepChange,
  showVideoTab,
  showChatTab,
  showSkilldar,
  skilldarNode,
  caption,
  videoNode,
  chatNode,
  tone,
  fill,
  narrow,
  showPoweredBy,
  poweredBy,
}) => {
  const list = (sectors || []).filter((sector) => sector?.careers?.length > 0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState('');
  const sectorKey = list.map((sector) => sector.name).join('|');
  useEffect(() => {
    setActiveIndex(0);
    setPage(0);
  }, [sectorKey]);
  const active = list[Math.min(activeIndex, Math.max(list.length - 1, 0))];

  // the row of sectors scrolls, with an arrow at either end while there is more to see
  const sectorsRef = useRef(null);
  const [edge, setEdge] = useState({ left: false, right: false });
  useEffect(() => {
    const element = sectorsRef.current;
    if (!element) return undefined;
    const update = () => setEdge({ left: element.scrollLeft > 2, right: element.scrollLeft + element.clientWidth < element.scrollWidth - 2 });
    update();
    element.addEventListener('scroll', update, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(element);
    return () => {
      element.removeEventListener('scroll', update);
      if (observer) observer.disconnect();
    };
  });
  useEffect(() => {
    const open = sectorsRef.current?.querySelector('[aria-pressed="true"]');
    if (open && open.scrollIntoView) open.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [activeIndex]);
  const scrollSectors = (step) => {
    const element = sectorsRef.current;
    if (element) element.scrollBy({ left: step * Math.max(element.clientWidth * 0.75, 120), behavior: 'smooth' });
  };

  const careers = active?.careers || [];
  const pageCount = Math.max(1, Math.ceil(careers.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = careers.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const goToPage = (next) => {
    setDirection(next > currentPage ? 'next' : 'prev');
    setPage(next);
  };
  const chooseSector = (index) => {
    setDirection('');
    setPage(0);
    setActiveIndex(index);
  };

  const tabs = TABS.filter((tab) => (tab.step !== 1 || showVideoTab) && (tab.step !== 2 || showChatTab));

  let content;
  if (currentStep === 1 && showVideoTab) {
    content = <div className="tsw-m-panel" role="tabpanel">{videoNode}</div>;
  } else if (currentStep === 2 && showChatTab) {
    content = <div className="tsw-m-panel" role="tabpanel" style={{ height: fill ? undefined : 360 }}>{chatNode}</div>;
  } else if (loading && !list.length) {
    content = <p className="tsw-m-empty" role="status"><CircularProgress size={18} color="inherit" /></p>;
  } else if (!list.length) {
    content = <p className="tsw-m-empty">There are no careers linked to this course yet.</p>;
  } else {
    content = (
      <>
        <div className="tsw-m-sectorwrap">
          {edge.left && (
            <button type="button" className="tsw-m-snav is-left" aria-label="Show earlier sectors" onClick={() => scrollSectors(-1)}>
              <ChevronIcon flip />
            </button>
          )}
          {edge.right && (
            <button type="button" className="tsw-m-snav is-right" aria-label="Show more sectors" onClick={() => scrollSectors(1)}>
              <ChevronIcon />
            </button>
          )}
          <div className={`tsw-m-sectors${edge.left ? ' fade-left' : ''}${edge.right ? ' fade-right' : ''}`} ref={sectorsRef}>
            {list.map((sector, index) => (
              <button
                type="button"
                key={sector.name}
                className="tsw-m-sector"
                aria-pressed={index === activeIndex}
                style={sector.color ? { '--sector': sector.color } : undefined}
                onClick={() => chooseSector(index)}
              >
                {sector.name}
                <span className="tsw-m-count">{sector.careers.length}</span>
              </button>
            ))}
          </div>
        </div>
        <ul className={`tsw-m-rows${direction ? ` dir-${direction}` : ''}`} key={`${active?.name}-${currentPage}`}>
          {visible.map((career, index) => {
            const salary = money(career.salary);
            const courses = Number(career.coursesAvailable);
            return (
              <li className="tsw-m-row" key={career.id || career.name} style={{ animationDelay: `${index * 22}ms` }}>
                <a
                  className="tsw-m-link"
                  href={`#career-${career.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenCareer(career.id);
                  }}
                >
                  <span className="tsw-m-name">{career.name}</span>
                  <span className="tsw-m-meta">
                    <span className="m-pay">{salary ? `${salary} a year` : ''}</span>
                    <span className="m-time">{career.transferWindow || ''}</span>
                    <span className="m-courses">{courses > 0 ? `${courses} ${courses > 1 ? 'courses' : 'course'}` : ''}</span>
                  </span>
                  <ArrowIcon className="tsw-m-arrow" />
                </a>
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  const showPager = currentStep === 0 && list.length > 0 && pageCount > 1;
  return (
    <div className={`tsw-m tone-${tone === 'light' ? 'light' : 'dark'}${fill ? ' is-fill' : ''}${narrow ? ' is-narrow' : ''}`}>
      <style>{STYLES}</style>
      <div className="tsw-m-head">
        <h2 className="tsw-m-title">{heading}</h2>
        {tabs.length > 1 && (
          <div className="tsw-m-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                type="button"
                role="tab"
                key={tab.step}
                className="tsw-m-tab"
                aria-selected={currentStep === tab.step}
                onClick={() => onStepChange(tab.step)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="tsw-m-body">
        <div className="tsw-m-main">
          {content}
          {(showPoweredBy || showPager) && (
            <div className="tsw-m-foot">
              {showPoweredBy ? <span>{poweredBy}</span> : <span />}
              {showPager && (
                <div className="tsw-m-pager">
                  <span className="tsw-m-range" aria-live="polite">
                    {currentPage * PAGE_SIZE + 1}-{Math.min((currentPage + 1) * PAGE_SIZE, careers.length)} of {careers.length}
                  </span>
                  <button type="button" className="tsw-m-nav" aria-label="Previous careers" disabled={currentPage === 0} onClick={() => goToPage(currentPage - 1)}>
                    <ChevronIcon flip />
                  </button>
                  <button type="button" className="tsw-m-nav" aria-label="Next careers" disabled={currentPage >= pageCount - 1} onClick={() => goToPage(currentPage + 1)}>
                    <ChevronIcon />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {showSkilldar && (
          <aside className="tsw-m-side" aria-label="Skilldar">
            <p className="tsw-m-label">Skilldar</p>
            <p className="tsw-m-hint">{caption}</p>
            <div className="tsw-m-chart">{skilldarNode}</div>
          </aside>
        )}
      </div>
    </div>
  );
};

MinimalLayout.propTypes = {
  sectors: PropTypes.array,
  loading: PropTypes.bool,
  heading: PropTypes.string,
  onOpenCareer: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepChange: PropTypes.func.isRequired,
  showVideoTab: PropTypes.bool,
  showChatTab: PropTypes.bool,
  showSkilldar: PropTypes.bool,
  skilldarNode: PropTypes.node,
  caption: PropTypes.string,
  videoNode: PropTypes.node,
  chatNode: PropTypes.node,
  tone: PropTypes.string,
  fill: PropTypes.bool,
  narrow: PropTypes.bool,
  showPoweredBy: PropTypes.bool,
  poweredBy: PropTypes.string,
};

export default MinimalLayout;
